import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { toastGetError, Toastify } from "../../../components/Toaster";
import { loadingSideEffects, updatingSideEffects, successSideEffects, errorSideEffects, addingSideEffects, deletingSideEffects, resetingSideEffects, RTKSideEffectSchema } from "..";

/**
 * --- Page Types & State ---
 */
export type PageType = "Auth" | "Profile" | "Workflow";

export interface BrandPage {
  id: string;
  BrandId: string;
  PageType: PageType;
  PageName: string | null;
  HtmlBody: string;
  CustomCss: string[];
  CustomJS: string[];
  // URL of the before-script the template runs. New (V3) templates ship a default
  // pointing at the content CDN; the preview popup keys the Authorize flow off it.
  BeforeScript?: string;
  CreatedDate: string;
  ModifiedDate: string;
}

export type BrandPageData = {
  [key in PageType]: BrandPage | null;
} & { BrandId?: string };

export interface PagesState extends RTKSideEffectSchema {
  Data: BrandPageData;
}

export interface CurrentPageState {
  Data: BrandPage;
  isLoading: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isSuccess: boolean;
  isError: boolean;
  errorMessage: string | null;
}

export interface AddPagePayload {
  BrandId: string;
  PageType: PageType;
  PageName?: string | null;
  HtmlBody: string;
  CustomCss?: string[];
  CustomJS?: string[];
}

export interface GetPagesPayload {
  brandId?: string;
}

export interface ResetPagesPayload {
  brandId: string;
  pages: string[];
}
export interface CodeSchema {
  status: "edit" | "add" | "delete" | "view";
  content: string;
  display: string;
  fileName?: string;
}

export interface UpdatePagePayload {
  BrandId?: string;
  Files: {
    Key: string;
    ContentObj: CodeSchema;
  }[];
}

export interface DeletePagePayload {
  BrandId?: string;
  pageType: string;
  section: string;
  fileName: string;
}

// --- Initial State ---
const initialPagesState: PagesState = {
  Data: {
    Auth: null,
    Profile: null,
    Workflow: null
  },
  isLoading: false,
  isAdding: false,
  isUpdating: false,
  isDeleting: false,
  isSuccess: false,
  isError: false,
  error: null
};

// --- Pages Slice ---
export const PagesSlice = createSlice({
  name: "brandPages",
  initialState: initialPagesState,
  reducers: {
    getPagesRequest(state, _: PayloadAction<GetPagesPayload>) {
      Object.assign(state, loadingSideEffects);
      state.Data = { Auth: null, Profile: null, Workflow: null };
    },
    getV2PagesRequest(state, _: PayloadAction<GetPagesPayload>) {
      Object.assign(state, loadingSideEffects);
      state.Data = { Auth: null, Profile: null, Workflow: null };
    },
    getPagesSuccess(state, action: PayloadAction<{ data: BrandPageData; isReset?: boolean }>) {
      Object.assign(state, successSideEffects);
      // Reset Data to initial (all null)
      state.Data = action.payload.data;
      if (action.payload.isReset) {
        Toastify("success", "Auth Studio reset successfully.");
      }
    },
    getPagesFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects);
      state.error = action.payload;
      toastGetError(action.payload);
    },

    addPageRequest(state, _: PayloadAction<AddPagePayload>) {
      Object.assign(state, addingSideEffects);
    },
    addPageSuccess(state, action: PayloadAction<BrandPage>) {
      Object.assign(state, successSideEffects);
      state.Data[action.payload.PageType] = action.payload;
      Toastify("success", "Page added successfully");
    },
    addPageFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects);
      state.error = action.payload;
      toastGetError(action.payload);
    },

    updatePageRequest(state, _: PayloadAction<UpdatePagePayload>) {
      Object.assign(state, updatingSideEffects);
    },
    updateV2PageRequest(state, _: PayloadAction<UpdatePagePayload>) {
      Object.assign(state, updatingSideEffects);
    },
    updatePageSuccess(state, action: PayloadAction<BrandPageData>) {
      Object.assign(state, successSideEffects);
      if (action.payload.Auth && Object.keys(action.payload.Auth).length > 0) {
        state.Data.Auth = action.payload.Auth;
      }
      if (action.payload.Profile && Object.keys(action.payload.Profile).length > 0) {
        state.Data.Profile = action.payload.Profile;
      }
      if (action.payload.Workflow && Object.keys(action.payload.Workflow).length > 0) {
        state.Data.Workflow = action.payload.Workflow;
      }
      Toastify("success", "Page updated successfully");
    },
    updatePageFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects);
      state.error = action.payload;
      toastGetError(action.payload);
    },

    deletePageRequest(state, _: PayloadAction<DeletePagePayload>) {
      Object.assign(state, deletingSideEffects);
    },
    deleteV2PageRequest(state, _: PayloadAction<DeletePagePayload>) {
      Object.assign(state, deletingSideEffects);
    },
    deletePageSuccess(state, action: PayloadAction<BrandPageData>) {
      Object.assign(state, successSideEffects);
      if (action.payload.Auth && Object.keys(action.payload.Auth).length > 0) {
        state.Data.Auth = action.payload.Auth;
      }
      if (action.payload.Profile && Object.keys(action.payload.Profile).length > 0) {
        state.Data.Profile = action.payload.Profile;
      }
      if (action.payload.Workflow && Object.keys(action.payload.Workflow).length > 0) {
        state.Data.Workflow = action.payload.Workflow;
      }
    },
    deletePageFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects);
      state.error = action.payload;
      toastGetError(action.payload);
    },
    resetV2PagesRequest(state, _: PayloadAction<string[]>) {
      Object.assign(state, resetingSideEffects);
      state.Data = { Auth: null, Profile: null, Workflow: null };
    },
    resetPagesRequest(state, _: PayloadAction<ResetPagesPayload>) {
      Object.assign(state, resetingSideEffects);
      state.Data = { Auth: null, Profile: null, Workflow: null };
    }
  }
});

// --- Actions ---
export const {
  getPagesRequest,
  getV2PagesRequest,
  getPagesSuccess,
  getPagesFailure,
  addPageRequest,
  addPageSuccess,
  addPageFailure,
  updatePageRequest,
  updateV2PageRequest,
  updatePageSuccess,
  updatePageFailure,
  deletePageRequest,
  deleteV2PageRequest,
  deletePageSuccess,
  deletePageFailure,
  resetV2PagesRequest,
  resetPagesRequest
} = PagesSlice.actions;

export default PagesSlice.reducer;
